import React from 'react'

import { Icon, Text } from '@mxenabled/mxui'

import { AppBar, IconButton, Stack, Toolbar, useTheme, useMediaQuery } from '@mui/material'
import { useGlobalCopyStore } from '../../../common'

const HEADER_HEIGHTS = {
  sm: 64,
  md: 80,
  lg: 92,
}

const HEADER_PADDING = {
  sm: '0px 16px',
  md: '0px 24px',
  lg: '0px 48px',
}

interface HeaderProps {
  title: string
  onBackButtonClick?: () => void
  children?: React.ReactNode
}

export const Header = ({ title, onBackButtonClick, children }: HeaderProps) => {
  const theme = useTheme()
  const { common: copy } = useGlobalCopyStore()

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const getHeaderStyles = () => {
    if (isSmallScreen) {
      return { variant: 'h3' as const, height: HEADER_HEIGHTS.sm, padding: HEADER_PADDING.sm }
    }
    if (isLargeScreen) {
      return { variant: 'h1' as const, height: HEADER_HEIGHTS.lg, padding: HEADER_PADDING.lg }
    }
    return { variant: 'h2' as const, height: HEADER_HEIGHTS.md, padding: HEADER_PADDING.md }
  }

  const headerStyles = getHeaderStyles()

  return (
    <AppBar
      color="inherit"
      position="sticky"
      sx={(theme) => ({
        boxShadow: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        justifyContent: 'center',
        height: headerStyles.height,
        padding: headerStyles.padding,
        zIndex: 900,
      })}
    >
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack alignItems="center" direction="row" gap={4}>
          {onBackButtonClick && (
            <IconButton aria-label={copy?.back} color="primary" onClick={onBackButtonClick}>
              <Icon color="primary" name="arrow_back_ios_new" size={24} />
            </IconButton>
          )}
          <Text variant={headerStyles.variant}>{title}</Text>
        </Stack>
        <Stack alignItems="center" direction="row">
          {children}
        </Stack>
      </Toolbar>
    </AppBar>
  )
}
